/**
 * Shared pill-style sub-navigation for the Settings section wrappers
 * (#851). One component, three call sites — Account / Workspace /
 * Organization wrappers each render this with their own filtered
 * tab list.
 *
 * Why one component instead of three per-section copies: the three
 * sub-navs differ only in their tab data. Sharing the renderer keeps
 * the pill styling + active-state semantics + danger-tab treatment
 * in one place — same reason `Admin/Agents/AgentsSubNav` ships as
 * one component for its five Agents-tier sub-pages.
 *
 * Why `NavLink` (not `useState` + `setActiveTab`): the destinations
 * are independent routes, each `mount`s its own data loader and
 * unmounts on switch. `NavLink` keeps `aria-current=page`, deep-
 * linking, and browser back/forward semantics working without
 * round-tripping through controlled tab state. Same rationale as
 * AgentsSubNav.tsx's "Why pills instead of Tabs" header comment.
 */
import { NavLink } from 'react-router-dom'
import { cn } from '@/lib/utils'

export interface SettingsTab {
  /** Absolute path under `/settings/<section>/*`. The full path —
   * not just the slug — so `NavLink`'s active-state match doesn't
   * have to reason about parent prefixes. Also the React key, so it
   * stays unique even for an `href` item that never routes. */
  to: string
  label: string
  /** Render in red. Used for "Delete account" / "Danger" tabs that
   * gate destructive actions. Matches the existing per-tab `danger`
   * flag in the current Settings shell. */
  danger?: boolean
  /** When set, the pill is a plain full-navigation link to this URL
   * rather than an in-app route, so `to` never routes. Used for the
   * Billing entry, whose destination is the billing page served on the
   * same site through the sign-in handoff, outside this SPA's router. */
  href?: string
}

export interface SettingsSubNavProps {
  tabs: readonly SettingsTab[]
  /** Accessible name for the nav landmark — section-specific so
   * screen readers can distinguish "Account tabs" from "Workspace
   * tabs" when more than one Settings section is visible (e.g., a
   * future preview surface that renders sections side-by-side).
   * Today only one section renders at a time so this is mostly
   * forward-defensive. */
  ariaLabel: string
}

export function SettingsSubNav({ tabs, ariaLabel }: SettingsSubNavProps) {
  if (tabs.length === 0) return null
  return (
    <nav
      aria-label={ariaLabel}
      className="flex flex-wrap gap-1 border-b pb-2"
    >
      {tabs.map((t) =>
        t.href ? (
          // A full-navigation link, not an in-app route: its destination is
          // served outside this SPA's router (the billing page, on the same
          // site). Styled like an inactive pill — it never holds active state
          // because leaving the app unmounts this nav.
          <a
            key={t.to}
            href={t.href}
            className={cn(
              'text-sm px-3 py-1.5 rounded-md transition-colors',
              'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {t.label}
          </a>
        ) : (
          <NavLink
            key={t.to}
            to={t.to}
            // `end` so parent routes don't claim active state when a
            // descendant is mounted. Matches AgentsSubNav's contract.
            end
            className={({ isActive }) =>
              cn(
                'text-sm px-3 py-1.5 rounded-md transition-colors',
                isActive
                  ? t.danger
                    ? 'bg-destructive text-destructive-foreground'
                    : 'bg-primary text-primary-foreground'
                  : t.danger
                    ? 'text-destructive/80 hover:bg-destructive/10 hover:text-destructive'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )
            }
          >
            {t.label}
          </NavLink>
        ),
      )}
    </nav>
  )
}
